import type { FC, ReactNode } from 'react'
import type { CurrencyCode, DiscountType, Invoice } from '@/types'

import { useEffect, useId, useState } from 'react'
import { Button } from '@/components/kit/button'
import { Input } from '@/components/kit/field'
import { Segmented } from '@/components/kit/segmented'
import { useCarWashActions } from '@/hooks/use-carwash-actions'
import { calculateDiscount } from '@/lib/carwash/billing'
import { formatMoney, getCurrencySymbol } from '@/lib/carwash/format'

interface DiscountEditorProps {
    invoice: Invoice
    currency: CurrencyCode
    onDone?: () => void
}

/* Flat or percentage discount on a pending bill, with a live preview of the new total. */
export const DiscountEditor: FC<DiscountEditorProps> = ({ invoice, currency, onDone }): ReactNode => {
    const actions = useCarWashActions()
    const inputId = useId()

    const [type, setType] = useState<DiscountType>(invoice.discountType)
    const [value, setValue] = useState(invoice.discountValue ? String(invoice.discountValue) : '')

    useEffect(() => {
        setType(invoice.discountType)
        setValue(invoice.discountValue ? String(invoice.discountValue) : '')
    }, [invoice.id, invoice.discountType, invoice.discountValue])

    const numeric = Number(value.replace(',', '.')) || 0
    const preview = calculateDiscount(invoice.subtotal, type, numeric)
    const invalid = numeric < 0 || (type === 'percent' && numeric > 100) || (type === 'amount' && numeric > invoice.subtotal)

    const apply = (next: number) => {
        if (actions.applyDiscount(invoice.id, type, next)) onDone?.()
    }

    return (
        <div className='space-y-3 rounded-xl border border-border bg-surface p-3'>
            <Segmented
                label='Discount type'
                size='sm'
                stretch
                value={type}
                onChange={setType}
                options={[
                    { value: 'amount', label: `Amount (${getCurrencySymbol(currency)})` },
                    { value: 'percent', label: 'Percent (%)' }
                ]}
            />

            <div className='flex items-start gap-2'>
                <div className='flex-1'>
                    <label htmlFor={inputId} className='sr-only'>
                        Discount value
                    </label>

                    <Input
                        id={inputId}
                        type='number'
                        inputMode='decimal'
                        min={0}
                        step='any'
                        placeholder={type === 'percent' ? 'e.g. 10' : 'e.g. 50'}
                        value={value}
                        onChange={event => setValue(event.target.value)}
                        aria-invalid={invalid}
                    />
                </div>

                <Button size='lg' disabled={invalid} onClick={() => apply(numeric)}>
                    Apply
                </Button>
            </div>

            <p className='text-[13px] text-muted-foreground'>
                {invalid
                    ? (type === 'percent' ? 'Use a value between 0 and 100.' : `The discount cannot be more than ${formatMoney(invoice.subtotal, currency)}.`)
                    : `Customer pays ${formatMoney(Math.max(0, invoice.subtotal - preview), currency)} after ${formatMoney(preview, currency)} off.`}
            </p>

            {invoice.discountValue > 0 && (
                <button
                    type='button'
                    onClick={() => apply(0)}
                    className='text-[13px] font-semibold text-red-700 hover:underline'
                >
                    Remove discount
                </button>
            )}
        </div>
    )
}
